import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import AuditSelect from '../../common/AuditSelect';
import { sortBy } from 'underscore';
import { navigate } from 'raviger';
import { useGetUserCompanies } from '../../../backend/query';
import { useGetLocationAuditScoreCards, useGetLocationAuditExecutionStatus, useGetLocationAuditComplianceItemStatus, useGetLocationAuditPerformanceMatrix } from '../../../backend/auditor';
import './LocationAuditDashboard.css';
import ModuleNotSubscribed from '../../common/ModuleNotSubscribed';
import PageLoader from '../../shared/PageLoader';

/* ===== Constants ===== */

const USER_COMPANIES_PAYLOAD = {
  include: [
    'associate_companies',
    'associate_companies.locations',
    'associate_companies.locations.city',
    'associate_companies.locations.state',
  ],
  filters: { is_active_eq: true },
};

const CURRENT_YEAR = String(new Date().getFullYear());
const CURRENT_MONTH_NAME = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
][new Date().getMonth()];

const _lastMonthDate = new Date();
_lastMonthDate.setMonth(_lastMonthDate.getMonth() - 1);
const LAST_MONTH_NAME = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
][_lastMonthDate.getMonth()];
const LAST_MONTH_YEAR = String(_lastMonthDate.getFullYear());

// Generate last 6 years descending
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) =>
  String(new Date().getFullYear() - i)
);

const FULL_MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

// time_range option definitions
type TimeRangeKey = 'full_year' | 'this_month' | 'last_month' | 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'custom';

interface TimeRangeOption {
  key: TimeRangeKey;
  label: string;
  showYear: boolean;
  showMonth: boolean;
}

const TIME_RANGE_OPTIONS: TimeRangeOption[] = [
  { key: 'full_year',  label: 'Full Year',             showYear: true,  showMonth: false },
  { key: 'this_month', label: 'This Month',             showYear: false, showMonth: false },
  { key: 'last_month', label: 'Last Month',             showYear: false, showMonth: false },
  { key: 'Q1',         label: 'Quarter 1 (Jan–Mar)',    showYear: true,  showMonth: false },
  { key: 'Q2',         label: 'Quarter 2 (Apr–Jun)',    showYear: true,  showMonth: false },
  { key: 'Q3',         label: 'Quarter 3 (Jul–Sep)',    showYear: true,  showMonth: false },
  { key: 'Q4',         label: 'Quarter 4 (Oct–Dec)',    showYear: true,  showMonth: false },
  { key: 'custom',     label: 'Custom Range',           showYear: true,  showMonth: true  },
];

/* ===== Payload builder ===== */

function buildScoreCardsPayload(
  companyId: string,
  assocId: string,
  locationId: string,
  timeRange: TimeRangeKey,
  year: string,
  months: string[],
): Record<string, any> | null {
  if (!companyId) return null;

  const p: Record<string, any> = { company_id: companyId };

  if (assocId !== 'all') p.associate_company_id = assocId;
  if (locationId !== 'all') p.location_id = locationId;

  if (timeRange === 'this_month') {
    p.time_range = 'this_month';
  } else if (timeRange === 'last_month') {
    p.time_range = 'last_month';
  } else if (timeRange === 'full_year') {
    p.time_range = 'full_year';
    p.year = year;
  } else if (timeRange === 'Q1' || timeRange === 'Q2' || timeRange === 'Q3' || timeRange === 'Q4') {
    p.time_range = timeRange;
    p.year = year;
  } else if (timeRange === 'custom') {
    p.year = year;
    if (months.length > 0) {
      p.months = months; // serialised as months[]=Jan&months[]=Feb by toRailsQuery
    }
  }

  return p;
}

/* ===== Performance matrix payload builder ===== */

const QUARTER_END: Record<string, string> = {
  Q1: 'March', Q2: 'June', Q3: 'September', Q4: 'December',
};

function buildMatrixPayload(
  companyId: string,
  assocId: string,
  locationId: string,
  timeRange: TimeRangeKey,
  year: string,
  months: string[],
): Record<string, any> | null {
  if (!companyId) return null;
  const p: Record<string, any> = { company_id: companyId };
  if (assocId !== 'all') p.associate_company_id = assocId;
  if (locationId !== 'all') p.location_id = locationId;
  if (timeRange === 'this_month') {
    p.year = CURRENT_YEAR;
    p.months = [CURRENT_MONTH_NAME];
  } else if (timeRange === 'last_month') {
    p.year = LAST_MONTH_YEAR;
    p.months = [LAST_MONTH_NAME];
  } else if (timeRange === 'custom') {
    p.year = year;
    if (months.length > 0) p.months = months;
  } else if (timeRange === 'Q1' || timeRange === 'Q2' || timeRange === 'Q3' || timeRange === 'Q4') {
    p.year = year;
    p.months = { Q1: ['January','February','March'], Q2: ['April','May','June'], Q3: ['July','August','September'], Q4: ['October','November','December'] }[timeRange];
  } else {
    // full_year
    p.year = year;
  }
  return p;
}

/* ===== Matrix score helpers ===== */

function parseMatrixScore(score: string | null | undefined): number | null {
  if (score === null || score === undefined || score === '') return null;
  const n = parseFloat(String(score).replace('%', ''));
  return isNaN(n) ? null : n;
}

function matrixYtd(
  scores: Array<{ overall_score: string | null; critical_score: string | null }>,
  type: 'overall' | 'critical',
  apiYtd: string | null | undefined,
): number | null {
  if (apiYtd !== null && apiYtd !== undefined && apiYtd !== '') {
    const v = parseMatrixScore(apiYtd);
    if (v !== null) return v;
  }
  const vals = scores
    .map(s => parseMatrixScore(type === 'overall' ? s.overall_score : s.critical_score))
    .filter(v => v !== null) as number[];
  if (vals.length === 0) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function formatMatrixScore(n: number): string {
  return n % 1 === 0 ? String(Math.round(n)) : n.toFixed(1);
}

/* ===== Execution status — API → donut entry mapping ===== */

const EXEC_BUCKETS = [
  { key: 'scheduled_count',               label: 'Scheduled',                       color: '#6b7280', statusKey: 'scheduled' },
  { key: 'preliminary_in_progress_count', label: 'In Progress',                     color: '#2965ad', statusKey: 'inprogress' },
  { key: 'preliminary_completed_count',   label: 'Preliminary Audit Completed',     color: '#ed8a34', statusKey: 'preliminary' },
  { key: 'final_completed_count',         label: 'Final Audit Completed',           color: '#14ae5c', statusKey: 'final' },
  { key: 'dropped_audits_count',          label: 'Dropped Audits (Auditee Absent)', color: '#c00f0c', statusKey: 'dropped' },
] as const;

function buildExecData(status: Record<string, number | undefined>): DonutEntry[] {
  return EXEC_BUCKETS
    .map(b => ({ label: b.label, value: status[b.key] || 0, color: b.color, statusKey: b.statusKey }))
    .filter(e => e.value > 0);
}

const COMP_BUCKETS = [
  { key: 'audited_count',        label: 'Audited',                  color: '#14ae5c', statusKey: 'audited' },
  { key: 'submitted_count',      label: 'Submitted (Under Review)', color: '#2965ad', statusKey: 'submitted' },
  { key: 'pending_count',        label: 'Pending',                  color: '#7c3aed', statusKey: 'pending' },
  { key: 'overdue_count',        label: 'Overdue',                  color: '#ed8a34', statusKey: 'overdue' },
  { key: 'rejected_count',       label: 'Rejected',                 color: '#975102', statusKey: 'rejected' },
  { key: 'dropped_count',        label: 'Dropped',                  color: '#c00f0c', statusKey: 'dropped' },
] as const;

function buildCompData(status: Record<string, number | undefined>): DonutEntry[] {
  return COMP_BUCKETS
    .map(b => ({ label: b.label, value: status[b.key] || 0, color: b.color, statusKey: b.statusKey }))
    .filter(e => e.value > 0);
}

/* ===== Donut Chart ===== */
interface DonutEntry {
  label: string;
  value: number;
  color: string;
  statusKey?: string;
}

function DonutChart({ data, centerLabel, clickable = true, onSliceClick }: {
  data: DonutEntry[];
  centerLabel: string;
  clickable?: boolean;
  onSliceClick?: (entry: DonutEntry) => void;
}) {
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const total = data.reduce((s, d) => s + d.value, 0);
  const R = 70;
  const C = 2 * Math.PI * R;

  let offset = 0;
  const segments = data.map(d => {
    const len = (d.value / total) * C;
    const seg = { ...d, len, offset, pct: ((d.value / total) * 100).toFixed(1) };
    offset += len;
    return seg;
  });

  return (
    <div className="lad-donut-svg-wrap" ref={wrapRef}>
      <svg className="lad-donut-svg" viewBox="0 0 180 180">
        <circle cx="90" cy="90" r={R} fill="none" stroke="#f1f5f9" strokeWidth="22" />
        {segments.map((seg, i) => (
          <circle
            key={i}
            className={`lad-donut-segment${clickable ? '' : ' non-clickable'}`}
            cx="90" cy="90" r={R}
            stroke={seg.color}
            strokeDasharray={`${seg.len.toFixed(2)} ${(C - seg.len).toFixed(2)}`}
            strokeDashoffset={`${(-seg.offset).toFixed(2)}`}
            onMouseMove={e => {
              if (!wrapRef.current) return;
              const rect = wrapRef.current.getBoundingClientRect();
              setTooltip({ text: `${seg.label}: ${seg.value} (${seg.pct}%)`, x: e.clientX - rect.left, y: e.clientY - rect.top });
            }}
            onMouseLeave={() => setTooltip(null)}
            onClick={clickable && onSliceClick ? () => onSliceClick(seg) : undefined}
          />
        ))}
      </svg>
      <div className="lad-donut-center">
        <div className="lad-donut-center-number">{total.toLocaleString()}</div>
        <div className="lad-donut-center-label">{centerLabel}</div>
      </div>
      {tooltip && (
        <div className="lad-donut-tooltip show" style={{ left: tooltip.x, top: tooltip.y }}>
          {tooltip.text}
        </div>
      )}
    </div>
  );
}

/* ===== Heatmap helpers ===== */
function heatClass(score: number | null): string {
  if (score == null) return 'empty';
  if (score >= 90) return 'green-light';
  if (score >= 75) return 'yellow-light';
  return 'red-light';
}

const ArrowUpRight = () => (
  <svg className="lad-heatmap-cell-arrow" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
    <line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/>
  </svg>
);

/* ===== KPI card skeleton ===== */
const KpiSkeleton = () => (
  <span className="lad-kpi-skeleton">&nbsp;</span>
);

/* ===== Main Component ===== */
function LocationAuditDashboard() {
  /* ── Company / Associate Company / Location cascade ── */
  const { userCompanies, isFetching: loadingCompanies } = useGetUserCompanies(USER_COMPANIES_PAYLOAD);

  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const defaultCompanyIdRef = useRef<string | null>(null);
  const restoredStateRef = useRef<any>(null);
  const restoreInProgressRef = useRef(false);

  // Restore filter state after back-navigation from /location-audits
  useEffect(() => {
    const saved = sessionStorage.getItem('locationAuditDashboardState');
    if (saved) {
      try {
        const s = JSON.parse(saved);
        restoredStateRef.current = s;
        if (s.timeRange) setTimeRange(s.timeRange as TimeRangeKey);
        if (s.selectedYear) setSelectedYear(s.selectedYear);
        if (Array.isArray(s.selectedMonths)) setSelectedMonths(s.selectedMonths);
        if (s.defaultCompanyId) defaultCompanyIdRef.current = s.defaultCompanyId;
      } catch {}
      sessionStorage.removeItem('locationAuditDashboardState');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [associateCompanies, setAssociateCompanies] = useState<any[]>([]);
  const [selectedAssocId, setSelectedAssocId] = useState('all');

  const [locationOptions, setLocationOptions] = useState<any[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState('all');

  useEffect(() => {
    if (loadingCompanies || !userCompanies) return;
    const sorted: any[] = sortBy(
      userCompanies.map((c: any) => ({ id: String(c.id), name: c.name, raw: c })),
      'name'
    );
    setCompanies(sorted);
    if (sorted.length > 0) {
      if (defaultCompanyIdRef.current === null) defaultCompanyIdRef.current = sorted[0].id;
      const restoredId = restoredStateRef.current && restoredStateRef.current.selectedCompanyId;
      const targetId = restoredId && sorted.find((c: any) => c.id === restoredId)
        ? restoredId
        : sorted[0].id;
      setSelectedCompanyId(targetId);
    }
  }, [loadingCompanies]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const co = companies.find(c => c.id === selectedCompanyId);
    if (!co) {
      // Only reset cascade when not mid-restore (e.g. initial mount with empty selectedCompanyId)
      if (!restoredStateRef.current) {
        setSelectedAssocId('all');
        setLocationOptions([]);
        setSelectedLocationId('all');
      }
      setAssociateCompanies([]);
      return;
    }
    setAssociateCompanies(
      sortBy(
        (co.raw.associate_companies || []).map((ac: any) => ({ id: String(ac.id), name: ac.name, raw: ac })),
        'name'
      )
    );
    if (restoredStateRef.current) {
      // Restoring: apply saved assocId; flag that assocId cascade should apply saved location
      setSelectedAssocId(restoredStateRef.current.selectedAssocId || 'all');
      restoreInProgressRef.current = true;
    } else {
      setSelectedAssocId('all');
      setLocationOptions([]);
      setSelectedLocationId('all');
    }
  }, [selectedCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!restoreInProgressRef.current) {
      setSelectedLocationId('all');
    }
    if (selectedAssocId === 'all') {
      setLocationOptions([]);
      if (restoreInProgressRef.current) {
        restoreInProgressRef.current = false;
        restoredStateRef.current = null;
      }
      return;
    }
    const ac = associateCompanies.find(a => a.id === selectedAssocId);
    if (!ac) { setLocationOptions([]); return; }
    setLocationOptions(
      sortBy(
        (ac.raw.locations || []).map((loc: any) => ({
          id: String(loc.id),
          name: loc.city ? `${loc.name}, ${loc.city.name}` : loc.name,
        })),
        'name'
      )
    );
    if (restoreInProgressRef.current && restoredStateRef.current) {
      setSelectedLocationId(restoredStateRef.current.selectedLocationId || 'all');
      restoreInProgressRef.current = false;
      restoredStateRef.current = null;
    }
  }, [selectedAssocId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Time range state ── */
  const [timeRange, setTimeRange] = useState<TimeRangeKey>('full_year');
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const monthPickerRef = useRef<HTMLDivElement>(null);

  const activeOption = TIME_RANGE_OPTIONS.find(o => o.key === timeRange)!;

  const handleTimeRangeChange = (key: TimeRangeKey) => {
    setTimeRange(key);
    // Clear custom-range selections whenever leaving custom
    if (timeRange === 'custom' && key !== 'custom') {
      setSelectedMonths([]);
      setMonthPickerOpen(false);
      setSelectedYear(CURRENT_YEAR);
    }
  };

  // Close month picker on outside click
  useEffect(() => {
    if (!monthPickerOpen) return;
    function handleOutside(e: MouseEvent) {
      if (monthPickerRef.current && !monthPickerRef.current.contains(e.target as Node)) {
        setMonthPickerOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [monthPickerOpen]);

  /* ── Score cards API ── */
  const scoreCardsPayload = useMemo(
    () => buildScoreCardsPayload(
      selectedCompanyId, selectedAssocId, selectedLocationId,
      timeRange, selectedYear, selectedMonths
    ),
    [selectedCompanyId, selectedAssocId, selectedLocationId, timeRange, selectedYear, selectedMonths]
  );

  const { scoreCards, isFetching: loadingCards } = useGetLocationAuditScoreCards(scoreCardsPayload);

  const { executionStatus, isFetching: loadingExec } = useGetLocationAuditExecutionStatus(scoreCardsPayload);
  const execData = buildExecData(executionStatus);

  const { complianceItemStatus, isFetching: loadingComp } = useGetLocationAuditComplianceItemStatus(scoreCardsPayload);
  const compData = buildCompData(complianceItemStatus);

  /* ── Performance matrix ── */
  const matrixPayload = useMemo(
    () => buildMatrixPayload(
      selectedCompanyId, selectedAssocId, selectedLocationId,
      timeRange, selectedYear, selectedMonths
    ),
    [selectedCompanyId, selectedAssocId, selectedLocationId, timeRange, selectedYear, selectedMonths]
  );

  const { matrixRows, isFetching: loadingMatrix } = useGetLocationAuditPerformanceMatrix(matrixPayload);

  /* ── Matrix / UI state ── */
  const [scoreMode, setScoreMode] = useState<'overall' | 'critical'>('overall');
  /* ── Download drawer ── */
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerDateRange, setDrawerDateRange] = useState('fy');

  /* ── Toast ── */
  const [toastMsg, setToastMsg] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((msg: string) => {
    setToastMsg(msg);
    setToastVisible(true);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastVisible(false), 3500);
  }, []);

  const hasActiveFilters =
    (defaultCompanyIdRef.current !== null && selectedCompanyId !== defaultCompanyIdRef.current) ||
    selectedAssocId !== 'all' ||
    selectedLocationId !== 'all' ||
    timeRange !== 'full_year' ||
    selectedYear !== CURRENT_YEAR ||
    selectedMonths.length > 0;

  function handleResetFilters() {
    sessionStorage.removeItem('locationAuditDashboardState');
    if (defaultCompanyIdRef.current) setSelectedCompanyId(defaultCompanyIdRef.current);
    setSelectedAssocId('all');
    setSelectedLocationId('all');
    setTimeRange('full_year');
    setSelectedYear(CURRENT_YEAR);
    setSelectedMonths([]);
    setMonthPickerOpen(false);
  }

  // Maps EXEC_BUCKETS statusKey → AuditListV2 AUDIT_STATUS_FILTER_OPTIONS value
  const EXEC_TO_LIST_STATUS: Record<string, string> = {
    scheduled:   'scheduled',
    inprogress:  'in_progress',
    preliminary: 'preliminary_completed',
    final:       'final_completed',
    dropped:     'dropped',
  };

  function buildLocationNavState() {
    const state: any = {};
    if (selectedCompanyId) state.company = { value: selectedCompanyId };
    if (selectedAssocId !== 'all') state.associate_company = { value: selectedAssocId };
    if (selectedLocationId !== 'all') state.location = { value: selectedLocationId };
    state.timeRange = timeRange.toLowerCase();
    state.year = selectedYear;
    if (timeRange === 'custom' && selectedMonths.length > 0) {
      state.months = selectedMonths.map(m => FULL_MONTHS.indexOf(m) + 1);
    }
    return state;
  }

  function saveDashboardState() {
    sessionStorage.setItem('locationAuditDashboardState', JSON.stringify({
      selectedCompanyId,
      selectedAssocId,
      selectedLocationId,
      timeRange,
      selectedYear,
      selectedMonths,
      defaultCompanyId: defaultCompanyIdRef.current,
    }));
  }

  function handleExecSliceClick(entry: DonutEntry) {
    const state = buildLocationNavState();
    if (entry.statusKey) state.auditStatus = EXEC_TO_LIST_STATUS[entry.statusKey] || entry.statusKey;
    saveDashboardState();
    navigate('/location-audits', { state });
  }

/* ── Derived display values ── */
  const overallScore = scoreCards.avg_overall_score;
  const criticalScore = scoreCards.avg_critical_score;
  const activeLocations = scoreCards.active_locations;
  const totalAudits = scoreCards.total_audits_ytd;
  const dueCount = scoreCards.due_audits_count;

  if (loadingCompanies) {
    return <PageLoader />;
  }

  if (userCompanies.length === 0) {
    return <ModuleNotSubscribed />;
  }

  return (
    <div className="lad-wrapper">

      {/* ── Page Header ── */}
      <div className="lad-page-header">
        <div>
          <h1 className="lad-page-title">Location Audit Dashboard</h1>
          <p className="lad-page-subtitle">Overview of compliance scores and audit statuses across all locations.</p>
        </div>
        {/* Download Report — not in current release
        <button className="lad-download-cta" onClick={() => setDrawerOpen(true)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          Download Report
        </button>
        */}
      </div>

      {/* ── Filter Row ── */}
      <div className="lad-filter-row">

        {/* Company */}
        <div className="lad-filter-group">
          <label className="lad-filter-label">Company</label>
          <AuditSelect
            options={companies.map(c => ({ value: c.id, label: c.name }))}
            value={companies.find(c => c.id === selectedCompanyId)
              ? { value: selectedCompanyId, label: companies.find(c => c.id === selectedCompanyId)!.name }
              : null}
            onChange={(opt: any) => opt && setSelectedCompanyId(opt.value)}
            isDisabled={loadingCompanies}
            isLoading={loadingCompanies}
          />
        </div>

        {/* Associate Company */}
        <div className="lad-filter-group">
          <label className="lad-filter-label">Associate Company</label>
          <AuditSelect
            options={[
              { value: 'all', label: 'All' },
              ...associateCompanies.map(ac => ({ value: ac.id, label: ac.name }))
            ]}
            value={
              selectedAssocId === 'all'
                ? { value: 'all', label: 'All' }
                : associateCompanies.find(a => a.id === selectedAssocId)
                  ? { value: selectedAssocId, label: associateCompanies.find(a => a.id === selectedAssocId)!.name }
                  : { value: 'all', label: 'All' }
            }
            onChange={(opt: any) => opt && setSelectedAssocId(opt.value)}
            isDisabled={associateCompanies.length === 0}
          />
        </div>

        {/* Location */}
        <div className="lad-filter-group">
          <label className="lad-filter-label">Location</label>
          <AuditSelect
            options={[
              { value: 'all', label: 'All' },
              ...locationOptions.map(loc => ({ value: loc.id, label: loc.name }))
            ]}
            value={
              selectedLocationId === 'all'
                ? { value: 'all', label: 'All' }
                : locationOptions.find(l => l.id === selectedLocationId)
                  ? { value: selectedLocationId, label: locationOptions.find(l => l.id === selectedLocationId)!.name }
                  : { value: 'all', label: 'All' }
            }
            onChange={(opt: any) => opt && setSelectedLocationId(opt.value)}
            isDisabled={selectedAssocId === 'all' || locationOptions.length === 0}
          />
        </div>

        {/* Time Range */}
        <div className="lad-filter-group">
          <label className="lad-filter-label">Duration</label>
          <AuditSelect
            options={TIME_RANGE_OPTIONS.map(o => ({ value: o.key, label: o.label }))}
            value={{ value: timeRange, label: TIME_RANGE_OPTIONS.find(o => o.key === timeRange)!.label }}
            onChange={(opt: any) => opt && handleTimeRangeChange(opt.value as TimeRangeKey)}
          />
        </div>

        {/* Year — shown for all ranges except This Month */}
        {activeOption.showYear && (
          <div className="lad-filter-group">
            <label className="lad-filter-label">Year</label>
            <AuditSelect
              options={YEAR_OPTIONS.map(y => ({ value: y, label: y }))}
              value={{ value: selectedYear, label: selectedYear }}
              onChange={(opt: any) => opt && setSelectedYear(opt.value)}
            />
          </div>
        )}

        {/* Months — multi-select popup, shown only for Custom Range */}
        {activeOption.showMonth && (
          <div className="lad-filter-group lad-month-picker-wrap" ref={monthPickerRef}>
            <label className="lad-filter-label">Months</label>
            <button
              type="button"
              className="lad-month-picker-trigger"
              onClick={() => setMonthPickerOpen(o => !o)}
            >
              <span>
                {selectedMonths.length === 0
                  ? 'All Months'
                  : selectedMonths.length <= 2
                    ? selectedMonths.map(m => m.slice(0, 3)).join(', ')
                    : `${selectedMonths.length} Months`}
              </span>
              {selectedMonths.length > 0 ? (
                <span
                  className="lad-month-picker-clear"
                  role="button"
                  aria-label="Clear months"
                  onClick={e => { e.stopPropagation(); setSelectedMonths([]); setMonthPickerOpen(false); }}
                >
                  ×
                </span>
              ) : (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M1 3L5 7L9 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
            {monthPickerOpen && (
              <div className="lad-month-picker-popup">
                {FULL_MONTHS.map(m => (
                  <button
                    key={m}
                    type="button"
                    className={'lad-month-cell' + (selectedMonths.includes(m) ? ' selected' : '')}
                    onClick={() => setSelectedMonths(prev =>
                      prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]
                    )}
                  >
                    {m.slice(0, 3)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {hasActiveFilters && (
          <OverlayTrigger placement="top" overlay={<Tooltip>Reset Filters</Tooltip>} delay={{ show: 300 } as any}>
            <button type="button" className="lad-reset-icon-btn" onClick={handleResetFilters} aria-label="Reset Filters">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="8" cy="8" r="6.5" />
                <line x1="5.5" y1="5.5" x2="10.5" y2="10.5" />
                <line x1="10.5" y1="5.5" x2="5.5" y2="10.5" />
              </svg>
            </button>
          </OverlayTrigger>
        )}
      </div>

      {/* ── KPI Cards ── */}
      <div className="lad-kpi-row">

        {/* Avg Overall Score */}
        <div className="lad-stat-card">
          <div className="lad-stat-icon-circle green">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#14ae5c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
          </div>
          <div className="lad-stat-info">
            <span className="lad-stat-number">
              {loadingCards ? <KpiSkeleton /> : overallScore != null ? `${overallScore}%` : '—'}
            </span>
            <span className="lad-stat-label">Avg Overall Score</span>
          </div>
        </div>

        {/* Avg Critical Score */}
        <div className="lad-stat-card">
          <div className="lad-stat-icon-circle red">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ec221f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path style={{ fill: 'none' }} d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <path style={{ fill: 'none' }} d="m9 12 2 2 4-4"/>
            </svg>
          </div>
          <div className="lad-stat-info">
            <span className="lad-stat-number">
              {loadingCards ? <KpiSkeleton /> : criticalScore != null ? `${criticalScore}%` : '—'}
            </span>
            <span className="lad-stat-label">Avg Critical Score</span>
          </div>
        </div>

        {/* Active Locations */}
        <div className="lad-stat-card">
          <div className="lad-stat-icon-circle blue">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2965ad" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
            </svg>
          </div>
          <div className="lad-stat-info">
            <span className="lad-stat-number">
              {loadingCards ? <KpiSkeleton /> : activeLocations != null ? activeLocations : '—'}
            </span>
            <span className="lad-stat-label">Active Locations</span>
          </div>
        </div>

        {/* Total Audits YTD */}
        <div className="lad-stat-card">
          <div className="lad-stat-icon-circle yellow">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#e5a000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path style={{ fill: 'none' }} d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
          </div>
          <div className="lad-stat-info">
            <span className="lad-stat-number">
              {loadingCards ? <KpiSkeleton /> : totalAudits != null ? totalAudits : '—'}
            </span>
            <span className="lad-stat-label">
              Total Audits{' '}
              <OverlayTrigger placement="top" overlay={<Tooltip>Yet to Done</Tooltip>} delay={{ show: 300 } as any}>
                <span style={{ borderBottom: '1px dashed currentColor', cursor: 'default' }}>YTD</span>
              </OverlayTrigger>
            </span>
            {!loadingCards && dueCount != null && dueCount > 0 && (
              <a
                className="lad-stat-sub-link"
                onClick={() => {
                  const dueThreshold = new Date();
                  dueThreshold.setDate(dueThreshold.getDate() + 7);
                  dueThreshold.setHours(23, 59, 59, 999);
                  const navState = buildLocationNavState();
                  delete navState.timeRange;
                  navState.auditStatuses = ['scheduled', 'in_progress', 'preliminary_completed'];
                  navState.dueDateBefore = dueThreshold.toISOString();
                  saveDashboardState();
                  navigate('/location-audits', { state: navState });
                }}
              >
                {dueCount} audit{dueCount !== 1 ? 's' : ''} due in the next 7 days
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="7" y1="17" x2="17" y2="7"/><polyline points="9 7 17 7 17 15"/>
                </svg>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* ── Donut Charts ── */}
      <div className="lad-donut-row">
        <div className="lad-donut-card">
          <div className="lad-donut-card-title">Audit Execution Status</div>
          {loadingExec ? (
            <div className="lad-donut-loading">
              <span className="lad-kpi-skeleton" style={{ width: 140, height: 140, borderRadius: '50%' }} />
            </div>
          ) : execData.length === 0 ? (
            <div className="lad-donut-empty">No audit data for selected filters.</div>
          ) : (
            <div className="lad-donut-body">
              <DonutChart
                data={execData}
                centerLabel="Total Audits"
                clickable={true}
                onSliceClick={handleExecSliceClick}
              />
              <div className="lad-donut-legend">
                {EXEC_BUCKETS.map((b, i) => {
                  const count = ((executionStatus as any) || {})[b.key] || 0;
                  const legendEntry: DonutEntry = { label: b.label, value: count, color: b.color, statusKey: b.statusKey };
                  return (
                    <div key={i} className="lad-legend-item clickable" onClick={() => handleExecSliceClick(legendEntry)}>
                      <span className="lad-legend-dot" style={{ background: b.color }} />
                      <span className="lad-legend-label">{b.label}</span>
                      <span className="lad-legend-count">{count}</span>
                      <svg className="lad-legend-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/>
                      </svg>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="lad-donut-card">
          <div className="lad-donut-card-title">Compliance Items Status</div>
          {loadingComp ? (
            <div className="lad-donut-loading">
              <span className="lad-kpi-skeleton" style={{ width: 140, height: 140, borderRadius: '50%' }} />
            </div>
          ) : compData.length === 0 ? (
            <div className="lad-donut-empty">No compliance data for selected filters.</div>
          ) : (
            <div className="lad-donut-body">
              <DonutChart
                data={compData}
                centerLabel="Total Items"
                clickable={false}
              />
              <div className="lad-donut-legend">
                {COMP_BUCKETS.map((b, i) => {
                  const entry = compData.find(e => e.statusKey === b.statusKey);
                  const count = entry ? entry.value : 0;
                  return (
                    <div key={i} className="lad-legend-item">
                      <span className="lad-legend-dot" style={{ background: b.color }} />
                      <span className="lad-legend-label">{b.label}</span>
                      <span className="lad-legend-count">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Performance Matrix ── */}
      <div className="lad-matrix-card">
        <div className="lad-matrix-header">
          <div className="lad-matrix-header-left">
            <div className="lad-matrix-title">Location-wise Month-wise Performance Matrix</div>
          </div>
          <div className="lad-matrix-header-center">
            <div className="lad-toggle-group">
              <button className={`lad-toggle-btn${scoreMode === 'overall' ? ' active' : ''}`} onClick={() => setScoreMode('overall')}>Overall Score</button>
              <button className={`lad-toggle-btn${scoreMode === 'critical' ? ' active' : ''}`} onClick={() => setScoreMode('critical')}>Critical Score</button>
            </div>
          </div>
          <div className="lad-matrix-header-right">
            <div className="lad-legend-row">
              <div className="lad-legend-item-color"><div className="lad-legend-swatch" style={{ background: 'rgba(236,34,31,0.3)' }} /><span>Below 75%</span></div>
              <div className="lad-legend-item-color"><div className="lad-legend-swatch" style={{ background: 'rgba(229,160,0,0.3)' }} /><span>75–89%</span></div>
              <div className="lad-legend-item-color"><div className="lad-legend-swatch" style={{ background: 'rgba(20,174,92,0.3)' }} /><span>90–100%</span></div>
            </div>
          </div>
        </div>
        {loadingMatrix ? (
          <div className="lad-matrix-loading">
            {[1,2,3].map(i => (
              <div key={i} className="lad-matrix-skeleton-row">
                <span className="lad-kpi-skeleton" style={{ width: 140, height: 28, borderRadius: 4 }} />
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(j => (
                  <span key={j} className="lad-kpi-skeleton" style={{ width: 52, height: 28, borderRadius: 4 }} />
                ))}
                <span className="lad-kpi-skeleton" style={{ width: 70, height: 28, borderRadius: 4 }} />
              </div>
            ))}
          </div>
        ) : matrixRows.length === 0 ? (
          <div className="lad-matrix-empty">No performance data for selected filters.</div>
        ) : (
          <div className="lad-heatmap-wrapper">
            {(() => {
              const colHeaders = (matrixRows[0].monthly_scores || []).map(ms =>
                ms.month.substring(0, 3) + " '" + ms.year.substring(2)
              );
              return (
                <table className="lad-heatmap-table">
                  <thead>
                    <tr>
                      <th>Location</th>
                      {colHeaders.map((h, i) => <th key={i}>{h}</th>)}
                      <th>YTD Avg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matrixRows.map((row, ri) => {
                      const ytdRaw = scoreMode === 'critical' ? row.ytd_critical_score : row.ytd_overall_score;
                      const ytdScore = matrixYtd(row.monthly_scores || [], scoreMode, ytdRaw);
                      return (
                        <tr key={ri}>
                          <td>
                            <div className="lad-loc-name">{row.location_name}</div>
                            {row.location_code ? <div className="lad-loc-code">{row.location_code}</div> : null}
                          </td>
                          {(row.monthly_scores || []).map((ms, ci) => {
                            const raw = scoreMode === 'critical' ? ms.critical_score : ms.overall_score;
                            const score = parseMatrixScore(raw);
                            if (score === null) {
                              return (
                                <td key={ci} className="lad-heatmap-td">
                                  <div className="lad-heatmap-cell empty">–</div>
                                </td>
                              );
                            }
                            return (
                              <td key={ci} className="lad-heatmap-td">
                                <div
                                  className={`lad-heatmap-cell ${heatClass(score)}${ms.audit_id ? ' cell-clickable' : ''}`}
                                  onClick={() => {
                                    if (ms.audit_id) navigate(`/location-audits/${ms.audit_id}`);
                                  }}
                                >
                                  {formatMatrixScore(score)}%<ArrowUpRight />
                                </div>
                              </td>
                            );
                          })}
                          <td className="lad-heatmap-td lad-ytd-td">
                            {ytdScore !== null ? (
                              <div
                                className={`lad-heatmap-cell ${heatClass(ytdScore)} cell-clickable`}
                                onClick={() => {
                                  const navState = buildLocationNavState();
                                  if (row.location_id) navState.location = { value: row.location_id };
                                  saveDashboardState();
                                  navigate('/location-audits', { state: navState });
                                }}
                              >
                                {formatMatrixScore(ytdScore)}%<ArrowUpRight />
                              </div>
                            ) : (
                              <div className="lad-heatmap-cell empty">–</div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              );
            })()}
          </div>
        )}
      </div>

      {/* ── Download Report Drawer ── */}
      <div className={`lad-drawer-backdrop${drawerOpen ? ' open' : ''}`} onClick={() => setDrawerOpen(false)} />
      <div className={`lad-drawer${drawerOpen ? ' open' : ''}`} role="dialog" aria-modal="true">
        <div className="lad-drawer-head">
          <div className="lad-drawer-title">Download Report</div>
          <button className="lad-drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div className="lad-drawer-body">
          <div className="lad-drawer-field">
            <label>Company<span className="req">*</span></label>
            <select>
              <option value="">Select company</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="lad-drawer-field">
            <label>Associate Company<span className="req">*</span></label>
            <select>
              <option value="">Select associate company</option>
              {associateCompanies.map(ac => <option key={ac.id} value={ac.id}>{ac.name}</option>)}
            </select>
          </div>
          <div className="lad-drawer-field">
            <label>Location<span className="req">*</span></label>
            <select>
              <option value="">Select location</option>
              {locationOptions.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
            </select>
          </div>
          <div>
            <div className="lad-drawer-section-label">
              Date Range<span style={{ color: '#EC221F', marginLeft: 1 }}>*</span>
            </div>
            <div className="lad-drawer-date-options">
              {[
                { key: 'fy',      label: 'Financial Year', hasSelect: true,  opts: ['FY 2026-27','FY 2025-26','FY 2024-25','FY 2023-24'] },
                { key: 'quarter', label: 'Quarter',         hasSelect: true,  opts: ['Q1 (Apr–Jun)','Q2 (Jul–Sep)','Q3 (Oct–Dec)','Q4 (Jan–Mar)'] },
                { key: 'last-1',  label: 'Last Month',      hasSelect: false, opts: [] },
                { key: 'last-3',  label: 'Last 3 Months',   hasSelect: false, opts: [] },
                { key: 'last-6',  label: 'Last 6 Months',   hasSelect: false, opts: [] },
              ].map(opt => (
                <div key={opt.key} className={`lad-drawer-date-option${drawerDateRange === opt.key ? ' selected' : ''}`} onClick={() => setDrawerDateRange(opt.key)}>
                  <span className="lad-drawer-radio" />
                  <span className="lad-drawer-date-option-text">{opt.label}</span>
                  {opt.hasSelect && (
                    <select className="lad-drawer-inline-select" onClick={e => e.stopPropagation()}>
                      {opt.opts.map(o => <option key={o}>{o}</option>)}
                    </select>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="lad-drawer-foot">
          <button className="lad-drawer-download-btn" onClick={() => { setDrawerOpen(false); showToast('Report is being generated. Once ready, it will be sent to your registered email.'); }}>
            Download
          </button>
        </div>
      </div>

      {/* ── Toast ── */}
      <div className={`lad-toast${toastVisible ? ' show' : ''}`}>{toastMsg}</div>
    </div>
  );
}

export default LocationAuditDashboard;
